import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useMessageEncryption } from '@/hooks/useMessageEncryption';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MessageSquare } from 'lucide-react';
import { ConversationView } from '@/components/ConversationView';
import { toast } from 'sonner';

interface Message {
  id: string;
  subject: string;
  content: string;
  message?: string;
  sender_id: string;
  recipient_id: string;
  is_read: boolean;
  created_at: string;
  sender?: {
    full_name: string;
  };
  recipient?: {
    full_name: string;
  };
}

export const VendorMessagingCenter = () => {
  const { user } = useAuth();
  const { decryptMessages } = useMessageEncryption();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState<{
    otherUserId: string;
    otherUserName: string;
    subject: string;
  } | null>(null);

  useEffect(() => {
    if (user?.id) {
      fetchMessages();
      
      const channel = supabase
        .channel('vendor-messages')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'messages',
            filter: `recipient_id=eq.${user.id}`
          },
          () => {
            fetchMessages();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user?.id]);

  const fetchMessages = async () => {
    if (!user?.id) return;

    try {
      console.log('VendorMessagingCenter - Fetching messages for user:', user.id);
      
      const { data, error } = await supabase
        .from('messages')
        .select(`
          *,
          sender:profiles!sender_id(full_name, system_role),
          recipient:profiles!recipient_id(full_name, system_role)
        `)
        .or(`sender_id.eq.${user.id},recipient_id.eq.${user.id}`)
        .order('created_at', { ascending: false });

      console.log('VendorMessagingCenter - Messages response:', { data, error, count: data?.length });

      if (error) {
        console.error('VendorMessagingCenter - Error fetching messages:', error);
        throw error;
      }
      
      // Decrypt messages if encrypted
      const decryptedMessages = data ? await decryptMessages(data as any) : [];
      setMessages(decryptedMessages as any);
    } catch (error) {
      console.error('Erreur lors du chargement des messages:', error);
      toast.error('Erreur lors du chargement des messages');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (messageId: string) => {
    try {
      const { error } = await supabase
        .from('messages')
        .update({ is_read: true })
        .eq('id', messageId);

      if (error) throw error;
      fetchMessages();
    } catch (error) {
      console.error('Erreur lors de la mise à jour du message:', error);
    }
  };

  const handleOpenMessage = (message: Message) => {
    const otherUserId = message.sender_id === user?.id ? message.recipient_id : message.sender_id;
    const otherUserName = message.sender_id === user?.id 
      ? message.recipient?.full_name || 'Client'
      : message.sender?.full_name || 'Client';
    
    setSelectedConversation({
      otherUserId,
      otherUserName,
      subject: message.subject
    });
    
    if (!message.is_read && message.recipient_id === user?.id) {
      handleMarkAsRead(message.id);
    }
  };

  // Group messages by conversation (same participants)
  const groupedMessages = messages.reduce((acc, msg) => {
    const otherUserId = msg.sender_id === user?.id ? msg.recipient_id : msg.sender_id;
    if (!acc[otherUserId]) {
      acc[otherUserId] = [];
    }
    acc[otherUserId].push(msg);
    return acc;
  }, {} as Record<string, Message[]>);

  const conversationsList = Object.entries(groupedMessages).map(([otherUserId, msgs]) => {
    const latestMsg = msgs[0];
    const unreadCount = msgs.filter(m => m.recipient_id === user?.id && !m.is_read).length;
    const otherUserName = latestMsg.sender_id === user?.id 
      ? latestMsg.recipient?.full_name || 'Client'
      : latestMsg.sender?.full_name || 'Client';
    
    return {
      otherUserId,
      otherUserName,
      latestMsg,
      unreadCount,
      messageCount: msgs.length
    };
  });

  const unreadCount = messages.filter(m => m.recipient_id === user?.id && !m.is_read).length;

  if (selectedConversation) {
    return (
      <ConversationView
        otherUserId={selectedConversation.otherUserId}
        otherUserName={selectedConversation.otherUserName}
        initialSubject={selectedConversation.subject}
        onBack={() => {
          setSelectedConversation(null);
          fetchMessages();
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Messages clients</h2>
          <p className="text-muted-foreground">
            Vos conversations avec les clients
          </p>
          {/* Debug info */}
          <p className="text-xs text-muted-foreground mt-2">
            Debug: User ID: {user?.id} | Messages count: {messages.length} | Loading: {loading.toString()}
          </p>
        </div>
        {unreadCount > 0 && (
          <Badge variant="destructive">{unreadCount} non lus</Badge>
        )}
      </div>

      <div className="space-y-4">
        {loading ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              Chargement...
            </CardContent>
          </Card>
        ) : conversationsList.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground flex flex-col items-center">
              <MessageSquare className="h-12 w-12 mb-4 opacity-50" />
              <p>Aucune conversation</p>
              <p className="text-xs mt-2">Total messages in DB: {messages.length}</p>
            </CardContent>
          </Card>
        ) : (
          conversationsList.map((conv) => (
            <Card 
              key={conv.otherUserId} 
              className={`cursor-pointer hover:shadow-md transition-shadow ${
                conv.unreadCount > 0 ? 'ring-2 ring-primary' : ''
              }`}
              onClick={() => handleOpenMessage(conv.latestMsg)}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-lg">
                        {conv.latestMsg.subject}
                      </CardTitle>
                      {conv.unreadCount > 0 && (
                        <Badge variant="default">{conv.unreadCount} nouveau(x)</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {conv.otherUserName} • {conv.messageCount} message{conv.messageCount > 1 ? 's' : ''}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(conv.latestMsg.created_at).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  <MessageSquare className="h-5 w-5 text-muted-foreground" />
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm line-clamp-2 text-muted-foreground">
                  {conv.latestMsg.sender_id === user?.id && 'Vous: '}
                  {conv.latestMsg.content || conv.latestMsg.message}
                </p>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};
